import React, { useEffect, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { CalendarMonthOutlined, CheckCircleOutlined, LinkOffOutlined, LockOutlined } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { useSearchParams } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import SettingsCard from "../Settings/SettingsCard";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  helper: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
  },
}));

const GoogleCalendarSettings = () => {
  const { classes } = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState({ connected: false, email: null, planEnabled: true });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const cargarStatus = async () => {
    try {
      const { data } = await api.get("/google-calendar/status");
      setStatus(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const resultado = searchParams.get("google");
    if (!resultado) return;

    if (resultado === "ok") {
      toast.success("Google Calendar conectado.");
    } else {
      toastError(new Error("No se pudo conectar con Google Calendar. Probá de nuevo."));
    }

    const next = new URLSearchParams(searchParams);
    next.delete("google");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const conectar = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const { data } = await api.get("/google-calendar/connect");
      window.location.href = data.url;
    } catch (err) {
      toastError(err);
      setConnecting(false);
    }
  };

  const desconectar = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("¿Desconectar Google Calendar? Los Prompts van a dejar de poder consultar o crear eventos.")) {
      return;
    }
    setDisconnecting(true);
    try {
      await api.delete("/google-calendar/disconnect");
      await cargarStatus();
      toast.success("Google Calendar desconectado.");
    } catch (err) {
      toastError(err);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) return null;

  return (
    <SettingsCard
      icon={CalendarMonthOutlined}
      title="Google Calendar"
      subtitle="Conectá una cuenta de Google para que los Prompts puedan consultar disponibilidad y crear eventos (citas, reservas, agendamiento) en ese calendario."
    >
    <Box className={classes.root}>
      {!status.planEnabled ? (
        <Box className={classes.row}>
          <Chip
            icon={<LockOutlined fontSize="small" />}
            label="No incluido en el plan actual"
            variant="outlined"
          />
          <Typography className={classes.helper}>
            Pedile a un administrador que actualice el plan de la empresa para habilitar esta función.
          </Typography>
        </Box>
      ) : status.connected ? (
        <Box className={classes.row}>
          <Chip
            icon={<CheckCircleOutlined />}
            label={status.email ? `Conectado: ${status.email}` : "Conectado"}
            color="success"
            variant="outlined"
          />
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<LinkOffOutlined fontSize="small" />}
            disabled={disconnecting}
            onClick={desconectar}
          >
            {disconnecting ? "Desconectando…" : "Desconectar"}
          </Button>
        </Box>
      ) : (
        <Box className={classes.row}>
          <Button
            variant="contained"
            startIcon={<CalendarMonthOutlined fontSize="small" />}
            disabled={connecting}
            onClick={conectar}
          >
            {connecting ? "Redirigiendo…" : "Conectar con Google"}
          </Button>
        </Box>
      )}
    </Box>
    </SettingsCard>
  );
};

export default GoogleCalendarSettings;
